/**
 * #3991: which connectivity toast (if any) should a debounced cloud-state change announce?
 *
 * Extracted because the `alreadyAnnouncedOffline` latch is precisely the thing that has misbehaved:
 *
 *  - It used to live on `cs-header-progress`, a PER-PAGE component. So a transition that happened
 *    while no page header was mounted was never announced — and never recovered, because the next
 *    header seeded its state as ALREADY Offline and therefore saw no transition at all. The latch
 *    reset on every page change too, so the back-online toast could vanish on its own.
 *
 * This helper answers exactly ONE question: did the state cross a boundary worth announcing?
 *
 * It deliberately does NOT decide whether the user has muted these toasts ("Never show again").
 * That belongs to the caller, and it must be asked at ANNOUNCE time, not here: the user can tap
 * "Never show again" on the offline toast we just raised, so a mute read any earlier is already out
 * of date. See `ConnectivityResumeFlow.announceCloudStateTransition`, which re-reads
 * `settings.isToastDismissed(...)` on the BackOnline edge.
 *
 * The pill renders the STANDING state; this decides the TRANSITION announcement. Keeping them
 * separate is the whole point — the pill must keep working for a user who turned the toasts off.
 */
export enum CloudStateToast {
  /** We have just gone offline: announce it. */
  WentOffline = 'went-offline',
  /** We are back, AND we actually told them we had gone. */
  BackOnline = 'back-online',
  /** Say nothing. */
  None = 'none',
}

export function decideCloudStateToast(input: {
  /** True when the previous debounced state was Offline. */
  wasOffline: boolean;
  /** True when the new debounced state is Offline. */
  isOffline: boolean;
  /** Did we announce the loss? Only then is a recovery worth announcing — we must never say "Back
   *  online" to someone we never told had gone offline.
   *
   *  NOT "is the toast muted". Whether the user has switched these toasts off is the caller's
   *  question, asked at announce time (see the module comment above). */
  alreadyAnnouncedOffline: boolean;
}): CloudStateToast {
  // a transition INTO offline
  if (input.isOffline && !input.wasOffline) return CloudStateToast.WentOffline;

  // a transition OUT of offline — but only worth saying if we said the opposite first
  if (!input.isOffline && input.wasOffline && input.alreadyAnnouncedOffline) {
    return CloudStateToast.BackOnline;
  }

  return CloudStateToast.None;
}
